import { z } from 'zod';
/**
 * Which vendor carries our outbound email (doc 29 D3).
 *
 * Same reasoning as {@link SmsProvider}: this is a fact about OUR vendor
 * contracts, not about a tenant, so it lives in code where `tsc` can prove the
 * registry covers every value and a vendor change is a reviewable diff rather
 * than a console edit nobody can date afterwards.
 *
 * Unlike SMS there is no per-country routing table. Email has no national
 * networks, no sender-field registration and no per-destination rate card — one
 * account reaches every mailbox in the world — so the choice is a single
 * `EMAIL_PROVIDER` param rather than a map, and its only job is letting us move
 * vendors (or fall back) without a code change.
 *
 * `resend` is the default because it is an HTTP API with no SDK, so the adapter
 * is a `fetch` call and the function bundle does not grow. `brevo` is here as
 * the EU-hosted alternative — the same shape, a different base URL and header
 * — which is what makes the port real rather than theoretical.
 *
 * `smtp` (doc 87) sends through a mailbox we own — today Hostinger's
 * `no-reply@topcall24.com`, whose SPF/DKIM the domain already publishes. It is
 * the one transport that needs a library (nodemailer): speaking SMTP by hand is
 * not a `fetch` call.
 */
export const EmailProvider = z.enum(['resend', 'brevo', 'smtp']);
/** `resend` / `brevo` / `smtp`, or null when the value is empty or unrecognised —
 *  which the dispatcher turns into a visible `no_provider` suppression rather
 *  than a crash, exactly as an unconfigured SMS vendor does. */
export function parseEmailProvider(raw) {
    const parsed = EmailProvider.safeParse((raw ?? '').trim().toLowerCase());
    return parsed.success ? parsed.data : null;
}
/**
 * Is this string addressable?
 *
 * Deliberately permissive — one `@`, something either side, a dot in the
 * domain, no spaces. A stricter regex is a well-known way to reject valid
 * addresses (new TLDs, `+` tags, quoted locals), and the authority on whether
 * an address exists is the mail server, not us. This exists to catch the
 * typo-shaped mistakes ("me@", "me at example.com") at the two points where
 * failing softly is possible: the portal's input, and the dispatcher's gate.
 */
export function isEmailAddress(value) {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > 254)
        return false;
    return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(trimmed);
}
//# sourceMappingURL=email.js.map